import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { paymentPollDelay } from '@/lib/polling';
import type { PortalApi } from './client';
import type { ActionItem, NewDiscussion, NewInitiative, RawAction } from './types';
import { httpApi, studioMessages } from './http';
import { mockApi } from './mock';

// Demo data by default; VITE_DEMO_DATA=false talks to portal-bff over /api.
export const isDemo = import.meta.env.VITE_DEMO_DATA !== 'false';
export const api: PortalApi = isDemo ? mockApi : httpApi;

export const useWorkspace = () => useQuery({ queryKey: ['workspace'], queryFn: api.workspace });
/** The signed-in client's role; undefined while the workspace loads. */
export const useClientRole = () => useWorkspace().data?.me.clientRole;
const isAction = (a: RawAction): a is ActionItem => a.kind !== 'moneyUnavailable';
export const useActions = () => useQuery({ queryKey: ['actions'], queryFn: api.actions, select: (all) => all.filter(isAction) });
/** True when the BFF could not reach the money backend: the invoices in the list are missing, not absent. */
export const useMoneyUnavailable = () =>
  useQuery({ queryKey: ['actions'], queryFn: api.actions, select: (all) => all.some((a) => a.kind === 'moneyUnavailable') }).data === true;
export const useSearch = (q: string) => useQuery({ queryKey: ['search', q], queryFn: () => api.search(q), enabled: q.trim().length >= 2 });
export const useProjects = (enabled = true) => useQuery({ queryKey: ['projects'], queryFn: api.projects, enabled });
export const useProject = (id: string) => useQuery({ queryKey: ['project', id], queryFn: () => api.project(id), enabled: !!id });
export const useActivity = (projectId: string) => useQuery({ queryKey: ['activity', projectId], queryFn: () => api.activity(projectId) });
export const useTasks = (projectId?: string) => useQuery({ queryKey: ['tasks', projectId ?? 'all'], queryFn: () => api.tasks(projectId) });
export const useTask = (id: string) => useQuery({ queryKey: ['task', id], queryFn: () => api.task(id), enabled: !!id });
export const useThreads = (projectId?: string, enabled = true) =>
  useQuery({ queryKey: ['threads', projectId ?? 'all'], queryFn: () => api.threads(projectId), refetchInterval: 15_000, enabled });
/** The invoices endpoint answers 403 to a member: ask only when the role reaches money. */
export const useInvoices = (enabled = true) => useQuery({ queryKey: ['invoices'], queryFn: api.invoices, refetchInterval: 60_000, refetchIntervalInBackground: false, enabled });
/**
 * Polls only while the payment is being processed, with growing pauses, for a few minutes at most;
 * a settled or untouched invoice is not asked about again. A hidden tab does not poll (TanStack's default).
 */
export function useInvoice(id: string) {
  const pending = useRef({ since: 0, attempt: 0 });
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.invoice(id),
    refetchIntervalInBackground: false,
    refetchInterval: (q) => {
      if (q.state.data?.status !== 'processing') {
        pending.current = { since: 0, attempt: 0 };
        return false;
      }
      if (!pending.current.since) pending.current.since = Date.now();
      return paymentPollDelay(pending.current.attempt++, Date.now() - pending.current.since);
    },
  });
}
export const useActs = () => useQuery({ queryKey: ['acts'], queryFn: api.acts });
export const useSubscriptions = () => useQuery({ queryKey: ['subscriptions'], queryFn: api.subscriptions });
export const useDocuments = (projectId?: string) =>
  useQuery({ queryKey: ['documents', projectId ?? 'all'], queryFn: () => api.documents(projectId) });
export const useSecrets = () => useQuery({ queryKey: ['secrets'], queryFn: api.secrets });

/** Pages go backwards in time: page 0 is the newest, each next page is earlier. */
export const useMessages = (threadId: string, studio = false) =>
  useInfiniteQuery({
    queryKey: ['messages', threadId, studio],
    // The studio reads any thread; the client endpoint is scoped to the client's own.
    queryFn: ({ pageParam }) => (studio && !isDemo ? studioMessages(threadId, pageParam) : api.messages(threadId, { before: pageParam })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasEarlier ? last.items[0]?.id : undefined),
    refetchInterval: 5000,
    enabled: !!threadId,
  });

/** Mutations invalidate broadly: the demo is small and stale counters confuse more than they save. */
function useInvalidateAll() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries();
}

export function useSendMessage(threadId: string) {
  const done = useInvalidateAll();
  return useMutation({ mutationFn: (v: { body: string; documentIds?: string[] }) => api.sendMessage(threadId, v.body, v.documentIds), onSuccess: done, meta: { inline: true } });
}
export function useCreateThread() {
  const done = useInvalidateAll();
  return useMutation({ mutationFn: (v: NewDiscussion) => api.createThread(v), onSuccess: done, meta: { inline: true } });
}
export function useApproveTask() {
  const done = useInvalidateAll();
  return useMutation({ mutationFn: api.approveTask, onSuccess: done });
}
export function useSignAct() {
  const done = useInvalidateAll();
  return useMutation({ mutationFn: (v: { id: string; name: string; consent: boolean }) => api.signAct(v.id, { name: v.name, consent: v.consent }), onSuccess: done, meta: { inline: true } });
}
export function useUploadActScan() {
  const done = useInvalidateAll();
  return useMutation({ mutationFn: (v: { id: string; file: File }) => api.uploadActScan(v.id, v.file), onSuccess: done, meta: { inline: true } });
}
export function useStartCheckout() {
  return useMutation({ mutationFn: (v: { invoiceId: string; method: 'card' | 'sbp' }) => api.startCheckout(v.invoiceId, v.method), meta: { inline: true } });
}
export function useCompleteCheckout() {
  const done = useInvalidateAll();
  return useMutation({ mutationFn: async (id: string) => (await api.checkoutStatus(id)).invoice, onSuccess: done });
}
/** Polls the bank for a checkout the client came back from; stops once it is no longer pending. */
export function useCheckoutStatus(id: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['checkout', id],
    enabled: !!id,
    queryFn: async () => {
      const r = await api.checkoutStatus(id!);
      if (r.checkout.status !== 'pending') qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'checkout' });
      return r;
    },
    refetchInterval: (q) => (q.state.data && q.state.data.checkout.status !== 'pending' ? false : 3000),
  });
}
export function useReportTransfer() {
  const done = useInvalidateAll();
  return useMutation({ mutationFn: api.reportTransfer, onSuccess: done });
}
export function useSendSecret() {
  const done = useInvalidateAll();
  return useMutation({ mutationFn: api.sendSecret, onSuccess: done, meta: { inline: true } });
}
export function useOpenSecret() {
  const done = useInvalidateAll();
  return useMutation({ mutationFn: api.openSecret, onSuccess: done, meta: { inline: true } });
}
export function useRerequestSecret() {
  const done = useInvalidateAll();
  return useMutation({ mutationFn: api.rerequestSecret, onSuccess: done, meta: { inline: true } });
}
export function useRevokeSecret() {
  const done = useInvalidateAll();
  return useMutation({ mutationFn: api.revokeSecret, onSuccess: done });
}
export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.markRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['threads'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

// --- Full client cycle ---------------------------------------------------------

export const useProposals = (enabled = true) => useQuery({ queryKey: ['proposals'], queryFn: api.proposals, enabled });
/** Proposals waiting for the client's decision: sent, still valid, not yet answered. */
export const isAwaitingDecision = (p: { status: string; validUntil: string }, today = new Date().toISOString().slice(0, 10)) => p.status === 'sent' && p.validUntil >= today;
export const useProposal = (id: string) => useQuery({ queryKey: ['proposal', id], queryFn: () => api.proposal(id) });
export const useSignRequests = () => useQuery({ queryKey: ['sign'], queryFn: api.signRequests });
export const useOnboarding = (projectId: string) => useQuery({ queryKey: ['onboarding', projectId], queryFn: () => api.onboarding(projectId) });
export const useChanges = (projectId?: string) => useQuery({ queryKey: ['changes', projectId ?? 'all'], queryFn: () => api.changes(projectId) });
export const useChange = (id: string, enabled = true) => useQuery({ queryKey: ['change', id], queryFn: () => api.change(id), enabled });
export const useReports = (projectId: string) => useQuery({ queryKey: ['reports', projectId], queryFn: () => api.reports(projectId) });
export const useMeetings = (projectId?: string) => useQuery({ queryKey: ['meetings', projectId ?? 'all'], queryFn: () => api.meetings(projectId) });
export const useNotifications = () => useQuery({ queryKey: ['notifications'], queryFn: api.notifications, refetchInterval: 10_000 });
export const usePrefs = () => useQuery({ queryKey: ['prefs'], queryFn: api.prefs });
export const usePayer = () => useQuery({ queryKey: ['payer'], queryFn: api.payer });
export const usePayers = () => useQuery({ queryKey: ['payers'], queryFn: api.payers });
export const usePayerDetails = (id: string) => useQuery({ queryKey: ['payer', id], queryFn: () => api.payerDetails(id) });
export const useColleagues = () => useQuery({ queryKey: ['colleagues'], queryFn: api.colleagues });
export const useStatementOfAccount = () => useQuery({ queryKey: ['statement-of-account'], queryFn: api.statementOfAccount });

/** `inline`: the screen renders this mutation's error itself, so no global toast. */
const m = <A, R>(fn: (a: A) => Promise<R>, inline = false) =>
  function useIt() {
    const done = useInvalidateAll();
    return useMutation({ mutationFn: fn, onSuccess: done, meta: { inline } });
  };

export const useAcceptProposal = m((id: string) => api.acceptProposal(id), true);
export const useDeclineProposal = m((v: { id: string; reason: string }) => api.declineProposal(v.id, v.reason), true);
export const useSign = m((v: { id: string; name: string }) => api.sign(v.id, v.name), true);
export const useCompleteOnboarding = m(api.completeOnboarding);
export const useRequestChange = m((v: { projectId: string; title: string; description: string } & NewInitiative) => {
  const { projectId, title, description, ...extra } = v;
  return api.requestChange(projectId, title, description, extra);
}, true);
export const useAnswerChange = m((v: { id: string; text: string }) => api.answerChange(v.id, v.text), true);
export const useDecideChange = m((v: { id: string; accept: boolean }) => api.decideChange(v.id, v.accept), true);
export const useReadNotifications = m(() => api.readNotifications());
export const useSavePrefs = m(api.savePrefs);
export const useSavePayer = m((p: Parameters<typeof api.savePayer>[0]) => api.savePayer(p), true);
export const useAddPayer = m((p: Parameters<typeof api.addPayer>[0]) => api.addPayer(p), true);
export const useUpdatePayer = m((v: { id: string; payer: Parameters<typeof api.updatePayer>[1] }) => api.updatePayer(v.id, v.payer), true);
export const useSetDefaultPayer = m((id: string) => api.setDefaultPayer(id), true);
export const useInviteColleague = m(api.inviteColleague, true);
export const useUpdateColleague = m((v: { id: string; patch: Parameters<typeof api.updateColleague>[1] }) => api.updateColleague(v.id, v.patch), true);
export const useResendInvitation = m(api.resendInvitation, true);
// Each file reports its own error on its status line, so no global toast.
export const useUpload = m((v: Parameters<typeof api.uploadDocument>[0]) => api.uploadDocument(v), true);
export const useSendFeedback = m((v: { projectId: string; score: number; comment: string }) => api.sendFeedback(v.projectId, v.score, v.comment));
export const useRenew = m(api.renew, true);

// «Интеграции» (MK42-6384): asked only by the owner while the API is on, else the BFF answers 403/404.
export const useApiKeys = (enabled = true) => useQuery({ queryKey: ['api-keys'], queryFn: api.apiKeys, enabled });
export const useCreateApiKey = m((v: Parameters<typeof api.createApiKey>[0]) => api.createApiKey(v), true);
export const useRotateApiKey = m((id: string) => api.rotateApiKey(id), true);
export const useRevokeApiKey = m((id: string) => api.revokeApiKey(id), true);
export const useApiDocs = (enabled = true) => useQuery({ queryKey: ['api-docs'], queryFn: api.apiDocs, enabled, staleTime: 5 * 60_000 });
// Webhooks (MK42-6388): the same owner and switch as the keys.
export const useWebhooks = (enabled = true) => useQuery({ queryKey: ['webhooks'], queryFn: api.webhooks, enabled });
export const useWebhookDeliveries = (filter: Parameters<typeof api.webhookDeliveries>[0], enabled = true) =>
  useQuery({ queryKey: ['webhooks', 'deliveries', filter], queryFn: () => api.webhookDeliveries(filter), enabled });
export const useCreateWebhook = m((v: Parameters<typeof api.createWebhook>[0]) => api.createWebhook(v), true);
export const useUpdateWebhook = m((v: { id: string; patch: Parameters<typeof api.updateWebhook>[1] }) => api.updateWebhook(v.id, v.patch), true);
export const useDeleteWebhook = m((id: string) => api.deleteWebhook(id), true);
export const useRotateWebhookSecret = m((id: string) => api.rotateWebhookSecret(id), true);
export const usePingWebhook = m((id: string) => api.pingWebhook(id), true);
export const useReplayWebhookDelivery = m((id: string) => api.replayWebhookDelivery(id), true);
export const useReplayFailedWebhooks = m((v: { id: string; since: string; until?: string }) => api.replayFailedWebhooks(v.id, v.since, v.until), true);
